import {
  InstitutionListResponse,
  TransmissionDemandDetail,
} from "../types/institution";
import { apiClient } from "./api";
import { SignatureResultDto } from "./omniAuthService";

/**
 * 기관 목록 및 전송요구서 연결 여부 조회
 * GET /transmission/institutions
 */
export const getInstitutionList =
  async (): Promise<InstitutionListResponse> => {
    const response = await apiClient.get<InstitutionListResponse>(
      "/transmission/institutions"
    );
    return response.data;
  };

/**
 * 전송요구서 상세 정보 조회
 * GET /transmission/:transmissionDemandId
 */
export const getTransmissionDemandDetail = async (
  transmissionDemandId: string
): Promise<TransmissionDemandDetail> => {
  const response = await apiClient.get<TransmissionDemandDetail>(
    `/transmission/${transmissionDemandId}`
  );
  return response.data;
};

/**
 * 전송요구서 철회
 * DELETE /transmission/:transmissionDemandId
 * @param transmissionDemandId 전송요구서 ID
 * @param deleteData 데이터 삭제 여부 (true: 채팅이력 및 의료정보 삭제, false: 전송요구서만 철회)
 */
export const deleteTransmissionDemand = async (
  transmissionDemandId: string,
  deleteData: boolean
): Promise<void> => {
  await apiClient.delete(
    `/transmission/${transmissionDemandId}?deleteData=${deleteData}`
  );
};

/**
 * 전송요구서 업데이트
 * PUT /transmission/:transmissionDemandId
 * @param transmissionDemandId 전송요구서 ID
 * @param signatureResult 서명 결과
 * @param deleteData 데이터 삭제 여부 (true: 채팅이력 및 의료정보 삭제, false: 전송요구서만 철회)
 */
export const updateTransmissionDemand = async (
  transmissionDemandId: string,
  signatureResult: SignatureResultDto,
  deleteData: boolean
): Promise<void> => {
  await apiClient.put(`/transmission/${transmissionDemandId}`, {
    signatureResult,
    deleteData,
  });
};

// 마이데이터 이력 유형
export enum TransmissionHistoryType {
  CANCEL_UTILIZATION = "CANCEL_UTILIZATION",
  MODIFY_TERMS_AGREEMENT_4_PROVISION = "MODIFY_TERMS_AGREEMENT_4_PROVISION",
  CREATE_DATASET_AGREEMENT_4_UTILIZATION = "CREATE_DATASET_AGREEMENT_4_UTILIZATION",
  MODIFY_DATASET_AGREEMENT_4_UTILIZATION = "MODIFY_DATASET_AGREEMENT_4_UTILIZATION",
  MODIFY_TERMS_REAGREEMENT_4_PROVISION_BY_USER_NO = "MODIFY_TERMS_REAGREEMENT_4_PROVISION_BY_USER_NO",
  CREATE_DEMAND_AGREEMENT_AND_TOKEN = "CREATE_DEMAND_AGREEMENT_AND_TOKEN",
  CREATE_DEMAND_AGREEMENT_AND_TOKEN_RECANTATION = "CREATE_DEMAND_AGREEMENT_AND_TOKEN_RECANTATION",
  FIND_DEMAND_TOKEN_MEDICAL_DATA_BY_PROVIDE_INSTITUTION = "FIND_DEMAND_TOKEN_MEDICAL_DATA_BY_PROVIDE_INSTITUTION",
  FIND_DEMAND_TOKEN_DIAGNOSTIC_REPORT_BY_USER_NO = "FIND_DEMAND_TOKEN_DIAGNOSTIC_REPORT_BY_USER_NO",
  FIND_DEMAND_TOKEN_EXPLANATION_OF_BENEFIT_BY_USER_NO = "FIND_DEMAND_TOKEN_EXPLANATION_OF_BENEFIT_BY_USER_NO",
  FIND_DEMAND_TOKEN_IMMUNIZATION_BY_USER_NO = "FIND_DEMAND_TOKEN_IMMUNIZATION_BY_USER_NO",
  FIND_DEMAND_TOKEN_MEDICATION_DISPENSE_BY_USER_NO = "FIND_DEMAND_TOKEN_MEDICATION_DISPENSE_BY_USER_NO",
}

// 마이데이터 이력 관련 타입
export interface TransmissionHistoryItem {
  /** 이력 유형 */
  historyType: string;
  /** 이력 제목 (화면 표시용) */
  title: string;
  /** 기관 명 */
  provideInstitutionName: string | null;
  /** 상태 (10: 정상, 20: 철회, 30: 만료) */
  status: string | null;
  /** 데이터셋 이름 목록 */
  dataSetNameList: string[] | null;
  /** 정기 전송 주기 */
  regularTransferCycle: string | null;
  /** 전송요구서 동의 시작일 */
  transmissionDemandAgreementBeginDate: string | null;
  /** 전송요구서 동의 종료일 */
  transmissionDemandAgreementEndDate: string | null;
  /** 동의된 약관 목록 */
  agreedTerms: string[] | null;
  /** 미동의된 약관 목록 */
  disagreedTerms: string[] | null;
  /** 등록된 항목 목록 */
  registeredItems: string[] | null;
  /** 철회된 항목 목록 */
  withdrawnItems: string[] | null;
  /** 정기 전송 여부 */
  isRegularTransfer: boolean | null;
  /** 성공 여부 */
  isSuccess: boolean | null;
  /** 생성 일시 */
  createdAt: string;
}

export interface TransmissionHistoryResponse {
  /** 마이데이터 이력 목록 */
  transmissions: TransmissionHistoryItem[];
}

/**
 * 마이데이터 이력 조회
 * GET /transmission/history
 */
export const getTransmissionHistory = async (): Promise<
  TransmissionHistoryItem[]
> => {
  const response = await apiClient.get<TransmissionHistoryResponse>(
    "/transmission/history"
  );
  return response.data.transmissions;
};
